import { create } from "zustand";

export type ScanSlotStatus = "pending" | "scan_now" | "scanned" | "reported" | "skipped" | "refund";

export type ScanSlot = {
  index: number;
  status: ScanSlotStatus;
};

type ScanProgressState = {
  scanSlots: Record<string, ScanSlot[]>;
};

type ScanProgressActions = {
  initializeSlots: (itemId: string, quantity: number) => void;
  markSlotScanned: (itemId: string) => void;
  markSlotReported: (itemId: string) => void;
  reactivateRemainingSlots: (itemId: string) => void;
  refundRemainingSlots: (itemId: string) => void;
  reset: () => void;
};

export type ScanProgressStore = ScanProgressState & ScanProgressActions;

const initialState: ScanProgressState = {
  scanSlots: {},
};

const createSlots = (quantity: number): ScanSlot[] =>
  Array.from({ length: quantity }, (_, i) => ({
    index: i,
    status: i === 0 ? "scan_now" : "pending",
  }));

const advanceSlot = (slots: ScanSlot[], completedStatus: "scanned" | "reported"): ScanSlot[] =>
  slots.map((slot, i, arr) => {
    if (slot.status === "scan_now") {
      return { ...slot, status: completedStatus };
    }
    // Activate next pending slot if the previous one was just completed
    if (slot.status === "pending" && i > 0 && arr[i - 1].status === "scan_now") {
      return { ...slot, status: "scan_now" };
    }
    return slot;
  });

const setRemainingStatus = (slots: ScanSlot[], status: "refund"): ScanSlot[] =>
  slots.map((slot) =>
    slot.status === "pending" || slot.status === "scan_now" ? { ...slot, status } : slot,
  );

export const useScanProgressStore = create<ScanProgressStore>()((set) => ({
  ...initialState,

  initializeSlots: (itemId, quantity) =>
    set((state) => ({
      scanSlots: {
        ...state.scanSlots,
        [itemId]: createSlots(quantity),
      },
    })),

  markSlotScanned: (itemId) =>
    set((state) => {
      const slots = state.scanSlots[itemId];
      if (!slots) return state;
      return {
        scanSlots: {
          ...state.scanSlots,
          [itemId]: advanceSlot(slots, "scanned"),
        },
      };
    }),

  markSlotReported: (itemId) =>
    set((state) => {
      const slots = state.scanSlots[itemId];
      if (!slots) return state;
      return {
        scanSlots: {
          ...state.scanSlots,
          [itemId]: advanceSlot(slots, "reported"),
        },
      };
    }),

  reactivateRemainingSlots: (itemId) =>
    set((state) => {
      const slots = state.scanSlots[itemId];
      if (!slots) return state;

      let firstPending = true;
      return {
        scanSlots: {
          ...state.scanSlots,
          [itemId]: slots.map((slot) => {
            if (slot.status === "pending" || slot.status === "scan_now") {
              if (firstPending) {
                firstPending = false;
                return { ...slot, status: "scan_now" as const };
              }
              return { ...slot, status: "pending" as const };
            }
            return slot;
          }),
        },
      };
    }),

  refundRemainingSlots: (itemId) =>
    set((state) => {
      const slots = state.scanSlots[itemId];
      if (!slots) return state;
      return {
        scanSlots: {
          ...state.scanSlots,
          [itemId]: setRemainingStatus(slots, "refund"),
        },
      };
    }),

  reset: () => set(initialState),
}));
